import { useCallback, useEffect, useRef, useState } from 'react';

interface Opts {
  silenceDelayMs: number;
  initialTimeoutMs?: number;
  onInterim?: (text: string) => void;
  onFinal: (text: string) => void;
  onError?: (msg: string) => void;
  onStopped?: (bySilence: boolean) => void;
}

/**
 * Voice-search hook. Opens a WebSocket to /api/ws/stt/stream and streams
 * 16 kHz Int16 PCM. Falls back to the Web Speech API if the server returns
 * close code 4503 or the WS never connects.
 */
export function useVoiceSearch(opts: Opts): {
  recording: boolean;
  toggle: () => void;
  supported: boolean;
} {
  const [recording, setRecording] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const speechDetectedRef = useRef(false);
  const silenceTimerRef = useRef<number | null>(null);
  const initialTimerRef = useRef<number | null>(null);
  // SpeechRecognition isn't in standard DOM types - treat as unknown-shaped.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fallbackRef = useRef<any | null>(null);

  const supported = typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

  const cleanup = useCallback((): void => {
    if (silenceTimerRef.current) window.clearTimeout(silenceTimerRef.current);
    if (initialTimerRef.current) window.clearTimeout(initialTimerRef.current);
    silenceTimerRef.current = null;
    initialTimerRef.current = null;
    try { processorRef.current?.disconnect(); } catch { /* */ }
    processorRef.current = null;
    try { ctxRef.current?.close(); } catch { /* */ }
    ctxRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      try { wsRef.current.close(); } catch { /* */ }
    }
    wsRef.current = null;
    try { fallbackRef.current?.stop(); } catch { /* */ }
    fallbackRef.current = null;
  }, []);

  const stop = useCallback((bySilence: boolean): void => {
    cleanup();
    setRecording(false);
    opts.onStopped?.(bySilence);
  }, [cleanup, opts]);

  const startFallback = useCallback((): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const SR = (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition;
    if (!SR) return false;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rec.onresult = (e: any): void => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (!r) continue;
        const alt = r[0];
        if (!alt) continue;
        const text = alt.transcript;
        if (r.isFinal) opts.onFinal(text);
        else opts.onInterim?.(text);
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rec.onerror = (e: any): void => {
      opts.onError?.(e.error || 'speech-recognition-error');
      stop(false);
    };
    rec.onend = (): void => stop(false);
    fallbackRef.current = rec;
    rec.start();
    return true;
  }, [opts, stop]);

  const start = useCallback(async (): Promise<void> => {
    if (recording) return;
    if (!supported) {
      opts.onError?.('Voice recognition is not supported in this browser.');
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { sampleRate: 16000, echoCancellation: true, noiseSuppression: true },
      });
    } catch (err) {
      const name = (err as { name?: string }).name;
      opts.onError?.(
        name === 'NotAllowedError'
          ? 'Microphone access denied. Please allow microphone access and try again.'
          : name === 'NotFoundError'
            ? 'No microphone found.'
            : 'Microphone error: ' + name,
      );
      return;
    }
    streamRef.current = stream;
    setRecording(true);
    speechDetectedRef.current = false;

    // Try WS first, fall back to Web Speech API on 4503 or failure.
    const wsUrl = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/api/ws/stt/stream`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    const connectTimeout = window.setTimeout(() => {
      if (ws.readyState !== WebSocket.OPEN) {
        try { ws.close(); } catch { /* */ }
        if (!startFallback()) {
          opts.onError?.('Voice service unavailable.');
          stop(false);
        }
      }
    }, 3000);

    ws.onopen = (): void => {
      window.clearTimeout(connectTimeout);
      initialTimerRef.current = window.setTimeout(() => {
        if (!speechDetectedRef.current) stop(false);
      }, opts.initialTimeoutMs ?? 10_000);

      // Pump audio
      const ac = new AudioContext();
      ctxRef.current = ac;
      const src = ac.createMediaStreamSource(stream);
      const proc = ac.createScriptProcessor(4096, 1, 1);
      processorRef.current = proc;
      const targetRate = 16000;
      proc.onaudioprocess = (ev: AudioProcessingEvent): void => {
        if (ws.readyState !== WebSocket.OPEN) return;
        const input = ev.inputBuffer.getChannelData(0);
        // Cheap downsample via stepping.
        const ratio = ac.sampleRate / targetRate;
        const outLen = Math.floor(input.length / ratio);
        const out = new Int16Array(outLen);
        for (let i = 0; i < outLen; i++) {
          const v = input[Math.floor(i * ratio)] ?? 0;
          out[i] = Math.max(-1, Math.min(1, v)) * 0x7fff;
        }
        ws.send(out.buffer);
      };
      src.connect(proc);
      proc.connect(ac.destination);
    };

    ws.onmessage = (ev: MessageEvent): void => {
      try {
        const data = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as {
          type?: string;
          text?: string;
          isFinal?: boolean;
        };
        if (data.type !== 'result' || !data.text) return;
        if (!speechDetectedRef.current) speechDetectedRef.current = true;
        if (silenceTimerRef.current) window.clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = window.setTimeout(() => stop(true), opts.silenceDelayMs);
        if (data.isFinal) opts.onFinal(data.text);
        else opts.onInterim?.(data.text);
      } catch { /* ignore non-JSON */ }
    };

    ws.onerror = (): void => {
      window.clearTimeout(connectTimeout);
    };
    ws.onclose = (ev: CloseEvent): void => {
      window.clearTimeout(connectTimeout);
      if (ev.code === 4503 || !speechDetectedRef.current) {
        if (!startFallback()) {
          if (ev.code === 4503) opts.onError?.('Voice service is not configured.');
          stop(false);
        }
        return;
      }
      if (recording) stop(false);
    };
  }, [opts, recording, startFallback, stop, supported]);

  const toggle = useCallback((): void => {
    if (recording) stop(false);
    else void start();
  }, [recording, start, stop]);

  useEffect(() => () => cleanup(), [cleanup]);

  return { recording, toggle, supported };
}
